import React from 'react'

import { Icon } from '@clientbase/clientbase-library'
import {
  Box,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  TooltipProps
} from '@mui/material'

import { formatMoney } from 'utils/FormatMoney'

import { theme } from 'styles/theme'

export type FilterType =
  | 'not_due'
  | 'overdue_less_than_7_days'
  | 'overdue_more_than_7_days'
  | 'churn'

export type TooltipLeadsCustomersProps = {
  children: React.ReactElement
  leads: number | string
  customers: number | string
  placement?: TooltipProps['placement']
  filterType?: FilterType
  cardTitle?: string
  onFilterClick?: (
    filterType: FilterType,
    customerType: 'lead' | 'customer'
  ) => void
}

const TooltipLeadsCustomers = ({
  children,
  leads,
  customers,
  placement = 'bottom',
  filterType,
  cardTitle,
  onFilterClick
}: TooltipLeadsCustomersProps) => {
  const handleFilterClick = (
    e: React.MouseEvent,
    customerType: 'lead' | 'customer'
  ) => {
    e.stopPropagation()
    if (filterType && onFilterClick) {
      onFilterClick(filterType, customerType)
    }
  }

  const tooltipContent = (
    <Box
      sx={{
        p: 1.5,
        minWidth: 200
      }}
    >
      <Stack gap={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box
            component="span"
            sx={{
              color: theme.palette.neutral[300],
              fontSize: '13px'
            }}
          >
            Leads
          </Box>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                color: theme.palette.neutral[100],
                fontSize: '13px'
              }}
            >
              {typeof leads === 'number' ? formatMoney(leads) : leads}
            </Box>
            {filterType && onFilterClick && (
              <Tooltip
                title={`Filtrar leads ${cardTitle || ''}`}
                placement="top"
              >
                <IconButton
                  size="small"
                  onClick={(e) => handleFilterClick(e, 'lead')}
                  sx={{
                    p: 0.25,
                    color: theme.palette.neutral[400],
                    '&:hover': {
                      color: theme.palette.primary.main,
                      bgcolor: 'transparent'
                    }
                  }}
                >
                  <Icon icon="filterAlt" size={1.8} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ borderColor: theme.palette.neutral[700] }} />

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box
            component="span"
            sx={{
              color: theme.palette.neutral[300],
              fontSize: '13px'
            }}
          >
            Clientes
          </Box>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                color: theme.palette.neutral[100],
                fontSize: '13px'
              }}
            >
              {typeof customers === 'number'
                ? formatMoney(customers)
                : customers}
            </Box>
            {filterType && onFilterClick && (
              <Tooltip
                title={`Filtrar clientes ${cardTitle || ''}`}
                placement="top"
              >
                <IconButton
                  size="small"
                  onClick={(e) => handleFilterClick(e, 'customer')}
                  sx={{
                    p: 0.25,
                    color: theme.palette.neutral[400],
                    '&:hover': {
                      color: theme.palette.primary.main,
                      bgcolor: 'transparent'
                    }
                  }}
                >
                  <Icon icon="filterAlt" size={1.8} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  )

  return (
    <Tooltip
      title={tooltipContent}
      placement={placement}
      arrow
      enterTouchDelay={0}
      leaveTouchDelay={3000}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: theme.palette.neutral[800],
            border: `1px solid ${theme.palette.neutral[600]}`,
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            '& .MuiTooltip-arrow': {
              color: theme.palette.neutral[800],
              '&::before': {
                border: `1px solid ${theme.palette.neutral[600]}`
              }
            }
          }
        }
      }}
    >
      {children}
    </Tooltip>
  )
}

export default TooltipLeadsCustomers
